import { createSelector } from 'reselect';
import { filter, forEach, flatten, uniqBy, sortBy, find } from 'lodash';
import { toDisplayString, timeToMs, msToDbTime } from '../../../lib/date.utils';
import { toSelectionFormat } from '../../../lib/collection.utils';
import { CLEARING_YES, CLEARING_LIMITED, CLEARING_TO_BE_CONFIRMED } from '../../../constants/settings';

const lineup = state => state.admin.lineup;

// RESELECT
export const shouldFetchLineupSelector = createSelector(lineup, lineup => !lineup.fetching && !lineup.fetched);
export const fetchingLineupSelector = createSelector(lineup, lineup => lineup.fetching);
export const stagesSelector = createSelector(lineup, lineup => lineup.stages);
export const entriesSelector = createSelector(lineup, lineup => lineup.entries);
export const artistOptionsSelector = createSelector(lineup, lineup => toSelectionFormat(lineup.artists));
export const isListViewSelector = createSelector(lineup, l => l.listView);
export const sortBySelector = createSelector(lineup, l => l.sortBy);
export const orderSelector = createSelector(lineup, l => l.order);
export const selectedEntriesSelector = createSelector(lineup, l => l.selectedEntries);

export const stageOptionsSelector = createSelector(stagesSelector, st =>
    toSelectionFormat(
        st.map(s => ({
            id: `${s.id}_${s.date}`,
            number: s.number,
            date: toDisplayString(s.date),
            stageName: s.name,
            name: `${s.number} - ${s.name} | ${toDisplayString(s.date)}`
        })),
        i => parseInt(i.number, 10)
    )
);

export const listViewEntriesSelector = createSelector(stagesSelector, stages => {
    return uniqBy(
        flatten(
            stages.map(s =>
                (s.performances || []).map(p => ({
                    start: p.startHour,
                    end: p.endHour,
                    artistName: (p.Artist && p.Artist.name) || '',
                    artistId: (p.Artist && p.Artist.id) || '',
                    id: p.id,
                    date: p.date,
                    stageId: s.id,
                    clearing: p.clearing,
                    stageName: s.name,
                    stageDate: msToDbTime(p.date)
                }))
            )
        ),
        x => x.id
    );
});

// DATA TRANSFORM
// STAGES
export function stagesToState(stages = []) {
    const stateStages = [];
    forEach(stages, s => {
        forEach(s.names || {}, (v, k) => {
            stateStages.push({ date: k, name: v, id: s.id, number: s.number, performances: s.Performances });
        });
    });
    return stateStages;
}
export function stageFormToAPIData(data) {
    const bodyData = { number: parseInt(data.number, 10), names: {} };
    (data.dates || []).forEach(d => (bodyData.names[d] = data[`name${d}`]));
    return bodyData;
}
export function stageToForm(stage, allStages) {
    const names = filter(allStages, s => s.id === stage.id);
    const form = { number: stage.number, id: stage.id, dates: names.map(x => x.date), namesIds: {} };
    forEach(names, v => {
        form[`name${v.date}`] = v.name;
        form.namesIds[v.date] = v.id;
    });
    return form;
}

// PERFORMANCES
export function performanceToAPIData(data) {
    const [stageId, date] = data.stageIdentifier.split('_');
    const bodyData = {
        stageId,
        date,
        artistId: data.artistId,
        clearing: data.clearing,
        startHour: data.start,
        endHour: data.end
    };
    return bodyData;
}

export function performanceToState(perf) {
    return {
        id: perf.id,
        stageIdentifier: `${perf.stageId}_${msToDbTime(perf.date)}`,
        artistId: perf.artistId,
        start: timeToMs(perf.startHour || perf.start),
        end: timeToMs(perf.endHour || perf.end),
        clearing: perf.clearing || []
    };
}

export function initPerformanceClearing(data, partners) {
    const clearing = [...data.clearing];
    partners.forEach(p => {
        if (!find(clearing, x => x.partnerId === p.id)) {
            clearing.push({
                partnerId: p.id,
                comments: '',
                useUntil: 0,
                live: CLEARING_TO_BE_CONFIRMED,
                nonLinear: CLEARING_TO_BE_CONFIRMED
            });
        }
    });
    return sortBy(
        uniqBy(
            filter(clearing, x => find(partners, p => x.partnerId === p.id)),
            c => c.partnerId
        ),
        c => ((find(partners, p => p.id === c.partnerId) || {}).name || '').toLowerCase()
    );
}

export function requireUseUntil(state = '') {
    return state.toLowerCase() === CLEARING_YES || state.toLowerCase() === CLEARING_LIMITED;
}



// WEBPACK FOOTER //
// ./src/selectors/admin/models/lineup.js